import { QuestionField } from './QuestionField';
import React, { useCallback, useState } from 'react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Plus, Trash2, Info } from 'lucide-react';
import { nanoid } from 'nanoid';

interface BlankSlot {
  id: string;
  position: number;
  acceptedAnswers: string[];
}

interface FillInBlanksEditorProps {
  prompt: string;
  blanks: BlankSlot[];
  onChange: (value: { prompt: string; blanks: BlankSlot[] }) => void;
}

/** Use `{{blank_id}}` syntax in the prompt to mark blanks. */
export function FillInBlanksEditor({
  prompt,
  blanks,
  onChange,
}: FillInBlanksEditorProps) {
  const onPromptChange = (next: string) => onChange({ prompt: next, blanks });
  const onBlanksChange = useCallback((next: BlankSlot[]) => onChange({ prompt, blanks: next }), [prompt, onChange]);
  const [newAnswer, setNewAnswer] = useState<Record<string, string>>({});

  const addBlank = useCallback(() => {
    const id = nanoid(6);
    const newBlank: BlankSlot = {
      id,
      position: blanks.length,
      acceptedAnswers: [],
    };
    onChange({ prompt: prompt + ` {{${id}}}`, blanks: [...blanks, newBlank] });
  }, [blanks, prompt, onChange]);

  const removeBlank = useCallback(
    (blankId: string) => {
      onChange({ blanks: blanks.filter((b) => b.id !== blankId), prompt: prompt.split(`{{${blankId}}}`).join('') });
    },
    [blanks, prompt, onChange],
  );

  const addAcceptedAnswer = useCallback(
    (blankId: string) => {
      const answer = newAnswer[blankId]?.trim();
      if (!answer) return;

      onBlanksChange(
        blanks.map((b) =>
          b.id === blankId
            ? { ...b, acceptedAnswers: [...b.acceptedAnswers, answer] }
            : b,
        ),
      );
      setNewAnswer((prev) => ({ ...prev, [blankId]: '' }));
    },
    [blanks, newAnswer, onBlanksChange],
  );

  const removeAcceptedAnswer = useCallback(
    (blankId: string, answerIndex: number) => {
      onBlanksChange(
        blanks.map((b) =>
          b.id === blankId
            ? {
                ...b,
                acceptedAnswers: b.acceptedAnswers.filter((_, i) => i !== answerIndex),
              }
            : b,
        ),
      );
    },
    [blanks, onBlanksChange],
  );

  return (
    <div className="fill-in-blanks-editor space-y-4">
      <div className="rounded-lg border border-border bg-muted/40 p-3">
        <div className="flex items-start gap-2">
          <Info size={16} className="mt-0.5 text-muted-foreground" />
          <div className="text-sm text-foreground">
            <p className="font-medium">How to create blanks:</p>
            <ol className="mt-1 list-inside list-decimal text-xs text-muted-foreground">
              <li>Click "Add Blank" to create a blank placeholder</li>
              <li>The placeholder (e.g., {"{{abc123}}"}) will be added to your prompt</li>
              <li>Add accepted answers for each blank below</li>
            </ol>
          </div>
        </div>
      </div>

      <div>
        <Label>Prompt Text (with blanks)</Label>
        <QuestionField name="prompt"><textarea
          value={prompt}
          onChange={(e) => onPromptChange(e.target.value)}
          className="mt-1 w-full rounded-md border p-3 text-sm"
          rows={4}
          placeholder="Enter your question text. Use {{blank_id}} to mark where blanks should appear..."
        /></QuestionField>
      </div>

      <div className="flex items-center justify-between">
        <Label>Blanks ({blanks.length})</Label>
        <QuestionField name="blanks"><Button type="button" variant="outline" size="sm" onClick={addBlank}>
          <Plus size={14} className="mr-1" />
          Add Blank
        </Button></QuestionField>
      </div>

      {blanks.length === 0 && (
        <p className="text-center text-sm text-gray-500">
          No blanks added yet. Click "Add Blank" to create one.
        </p>
      )}

      <div className="space-y-4">
        {blanks.map((blank, index) => (
          <div key={blank.id} className="rounded-lg border bg-gray-50 p-4">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-sm font-medium">
                Blank {index + 1}: <code className="text-xs font-mono font-semibold text-foreground">{`{{${blank.id}}}`}</code>
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label={`Remove blank ${index + 1}`}
                onClick={() => removeBlank(blank.id)}
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 size={14} />
              </Button>
            </div>

            <div className="space-y-2">
              <Label className="text-xs">Accepted Answers</Label>
              <div className="flex flex-wrap gap-2">
                {blank.acceptedAnswers.map((answer, answerIndex) => (
                  <span
                    key={answerIndex}
                    className="inline-flex items-center gap-1 rounded-md bg-green-100 px-3 py-1 text-sm text-green-800"
                  >
                    {answer}
                    <button
                      type="button"
                      onClick={() => removeAcceptedAnswer(blank.id, answerIndex)}
                      className="ml-1 hover:text-green-600"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <QuestionField name={`blanks.${index}.acceptedAnswers`} className="flex-1"><Input
                  value={newAnswer[blank.id] || ''}
                  onChange={(e) =>
                    setNewAnswer((prev) => ({ ...prev, [blank.id]: e.target.value }))
                  }
                  placeholder="Add accepted answer..."
                  className="h-8 text-sm"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addAcceptedAnswer(blank.id);
                    }
                  }}
                /></QuestionField>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => addAcceptedAnswer(blank.id)}
                >
                  Add
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default FillInBlanksEditor;
